import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {PageTypes, useCurrentPageType, useHistory} from '@aboutyou/router';
import {getIsConsentBannerAccepted} from '@aboutyou/storage';
import {GTMTrackingClient, useTrackFeature} from '@aboutyou/tracking';
import {SearchResultQuestionnaireStep} from '@aboutyou/ui-components';
import {useContext, useEffect, useState} from 'react';
import {SearchSuggestionState} from '../../../backend-services/Header/SearchBar/hooks/useSearchSuggestions';

export const useSearchResultQuestionnaire = () => {
  const trackFeature = useTrackFeature();
  const currentPageType = useCurrentPageType();

  const {getSearchEventParams} = useContext(GTMTrackingClient);
  const GTMSearchEventParams = getSearchEventParams();
  const {searchTerm, searchName, searchType} = {
    ...GTMSearchEventParams,
    searchType:
      GTMSearchEventParams?.searchType ??
      getSearchTypeByCurrentPage(currentPageType),
  };

  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const {isActive: isFeatureFlagActive} = useIsFeatureActive(
    'search_result_questionnaire',
  );

  const [step, setStep] = useState<SearchResultQuestionnaireStep>('initial');

  useEffect(() => {
    // Since the SearchResultQuestionnaire is inject into App level
    // we need to reset the step when the page changes because we want to display the SearchResultQuestionnaire
    // if the user had previously viewed the questionnaire and begins searching again.
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setStep('initial');
  }, [currentPageType]);

  const shouldShowSearchResultQuestionnaire =
    isFeatureFlagActive &&
    getIsConsentBannerAccepted() &&
    ((history.location.state as SearchSuggestionState)?.wasSearchPerformed ||
      searchParams.has('term')) &&
    step !== 'closed';

  const onClose = () => {
    setStep('closed');
  };

  const onThumbsUpClick = () => {
    setStep('thankYou');

    trackFeature({
      eventType: 'like',
      label: `sr_quest|${searchTerm}|${searchName}|${searchType}|`,
    });
  };

  const onThumbsDownClick = () => {
    setStep('optionSelection');

    trackFeature({
      eventType: 'dislike',
      label: `sr_quest|${searchTerm}|${searchName}|${searchType}|`,
    });
  };

  const onFirstOptionSelected = () => {
    setStep('thankYou');

    trackFeature({
      eventType: 'submit',
      label: `sr_quest|${searchTerm}|${searchName}|${searchType}|opt1`,
    });
  };

  const onSecondOptionSelected = () => {
    setStep('feedbackInput');

    trackFeature({
      eventType: 'submit',
      label: `sr_quest|${searchTerm}|${searchName}|${searchType}|opt2`,
    });
  };

  const onSaveAndCloseClick = (answer: string) => {
    setStep('thankYou');

    const sanitizedAnswer = answer.trim().replace(/(<([^>]+)>)/gi, '');

    trackFeature({
      eventType: 'submit',
      label: `sr_quest|${searchTerm}|${searchName}|${searchType}|${sanitizedAnswer}`,
    });
  };

  return {
    shouldShowSearchResultQuestionnaire,
    step,
    onThumbsUpClick,
    onThumbsDownClick,
    onFirstOptionSelected,
    onSecondOptionSelected,
    onClose,
    onSaveAndCloseClick,
  };
};

const getSearchTypeByCurrentPage = (currentPageType?: PageTypes) => {
  if (
    currentPageType &&
    ['brandShop', 'brandsOverview', 'category', 'story'].includes(
      currentPageType,
    )
  ) {
    return 'bi_search';
  }

  return 'srp';
};
